import { useMediaQuery } from 'react-responsive';

import { DeviceType } from '@cineamo/api/constants/DeviceType.enum';

import { tailwindBreakpoints } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

/**
 * Frozen at module evaluation and therefore hydration-unsafe: the server evaluates them once with
 * no window at all. Only safe inside event handlers and effects — prefer the hooks below.
 */
export const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
export const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

const isBrowser = typeof window !== 'undefined' && window.document?.createElement;

const supportsTouchEvents = isBrowser && 'ontouchstart' in window;

export const isTouchScreen = supportsTouchEvents || (isBrowser && window.navigator.maxTouchPoints > 1);

export type TailwindSizes = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export type UseScreenSizeReturn = Record<TailwindSizes | string, boolean>;

/**
 * react-responsive reads the media query synchronously inside its `useState` initialisers, so
 * without the mount gate these hooks return the real viewport already in the hydration render while
 * the server rendered the no-window fallback. Anything branching on them then produced a different
 * tree or attribute than the server HTML (CIN-14426). Until mounted they therefore report the same
 * value the server did — `useIsMobile` false, `useScreenSize`/`useDeviceType` the smallest
 * breakpoint — which means first paint has to come from CSS (`lg:` variants,
 * MobileOrDesktopRendererSSR), not from these hooks.
 */
export function useIsMobile(breakpoint: TailwindSizes = 'lg'): boolean {
    const isMounted = useIsMounted();
    const mobileBreakpoint: string = (tailwindBreakpoints as Record<string, string>)?.[breakpoint] || '1024px';
    return useMediaQuery({ query: `(max-width: ${mobileBreakpoint})` }) && isMounted;
}

export function useScreenSize(): UseScreenSizeReturn {
    const isMounted = useIsMounted();
    const breakpoints: Record<TailwindSizes, string> = tailwindBreakpoints;
    const screenSizeMap: Record<TailwindSizes | string, boolean> = {};
    Object.keys(breakpoints).forEach((breakpoint) => {
        screenSizeMap[breakpoint] =
            useMediaQuery({ query: `(min-width: ${breakpoints[breakpoint as TailwindSizes]})` }) && isMounted;
    });
    if (!Object.values(screenSizeMap).some((value) => value)) {
        screenSizeMap['sm'] = true;
    }
    return {
        ...screenSizeMap
    };
}

export function useDeviceType(): DeviceType {
    const screenSizeMap = useScreenSize();
    if (screenSizeMap['lg'] || screenSizeMap['xl'] || screenSizeMap['2xl']) {
        return DeviceType.DESKTOP;
    } else if (screenSizeMap['md']) {
        return DeviceType.TABLET;
    } else {
        return DeviceType.MOBILE;
    }
}
